/**
 * features/notifications/UploadReminderRecipients.tsx
 * ------------------------------------------------------------------
 * The "who is reminded" panel on each upload screen.
 *
 * Shows when the weekly reminder goes out and to whom. An admin can
 * change the list (accounts are picked from the user directory, which
 * only admin and head stream — see DataContext); everybody else sees it
 * read-only. With nothing saved, the Cloud Function reminds holders of
 * the default roles, and this panel says so rather than showing an
 * empty list.
 *
 * Rules: firestore.rules → reportUploadReminders.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { BellRing, Pencil, Check, X } from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { ROLES } from '@/lib/permissions';
import { SoftCard, AccentButton, glassFieldClass } from '@/features/finance/ui';
import {
  UPLOAD_REMINDERS, UPLOAD_REMINDERS_COLLECTION, defaultRecipients,
  type ReminderRecipient, type UploadReminderKind, type UploadReminderSettings,
} from '@/lib/uploadReminders';

export default function UploadReminderRecipients({ kind }: { kind: UploadReminderKind }) {
  const { userData, userRoles } = useAuth();
  const { users } = useData();
  const cfg = UPLOAD_REMINDERS[kind];
  const isAdmin = userRoles.some((r) => r === 'admin' || r === 'systemAdmin');
  const [saved, setSaved] = useState<ReminderRecipient[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => onSnapshot(
    doc(db, UPLOAD_REMINDERS_COLLECTION, kind),
    (snap) => setSaved(((snap.data() as UploadReminderSettings | undefined)?.recipients) ?? []),
    () => setSaved([]),
  ), [kind]);

  const staffUsers = useMemo(
    () => users.filter((u) => u.role !== 'parent').sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email)),
    [users],
  );
  const fallback = useMemo(() => defaultRecipients(kind, staffUsers), [kind, staffUsers]);
  const roleNames = cfg.defaultRoles.map((r) => ROLES[r]?.label ?? r).join(' or ');

  function startEdit() {
    setPicked(new Set((saved?.length ? saved : fallback.map((u) => ({ uid: u.uid }))).map((r) => r.uid)));
    setFilter('');
    setError('');
    setEditing(true);
  }

  async function save() {
    setBusy(true);
    setError('');
    try {
      const recipients: ReminderRecipient[] = staffUsers
        .filter((u) => picked.has(u.uid))
        .map((u) => ({ uid: u.uid, name: u.displayName || u.email, email: u.email }));
      await setDoc(doc(db, UPLOAD_REMINDERS_COLLECTION, kind), {
        recipients,
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid ?? 'unknown',
      });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const shown = editing
    ? staffUsers.filter((u) => `${u.displayName} ${u.email}`.toLowerCase().includes(filter.trim().toLowerCase()))
    : [];

  return (
    <SoftCard hover={false}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <BellRing size={16} className="text-[var(--accent-link)] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Weekly reminder — {cfg.when}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Bell, push notification and email, opening this screen.{' '}
              {saved === null ? 'Loading…' : saved.length > 0
                ? <>Goes to {saved.map((r) => r.name).join(', ')}.</>
                : <>No one chosen yet, so it goes to every {roleNames} account{fallback.length ? ` (${fallback.map((u) => u.displayName || u.email).join(', ')})` : ''}.</>}
            </p>
          </div>
        </div>
        {isAdmin && !editing && (
          <button onClick={startEdit} className="flex items-center gap-1 text-xs font-medium text-[var(--accent-link)] hover:underline shrink-0">
            <Pencil size={12} /> Change
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-3 space-y-2">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search staff accounts…" className={glassFieldClass()} />
          <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50">
            {shown.map((u) => (
              <label key={u.uid} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <input
                  type="checkbox"
                  checked={picked.has(u.uid)}
                  onChange={(e) => setPicked((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(u.uid); else next.delete(u.uid);
                    return next;
                  })}
                />
                <span className="text-gray-800 dark:text-gray-100">{u.displayName || u.email}</span>
                <span className="text-xs text-gray-400 truncate">{u.email}</span>
              </label>
            ))}
            {shown.length === 0 && <p className="px-3 py-3 text-xs text-gray-400">No matching accounts.</p>}
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex gap-2">
            <AccentButton icon={Check} disabled={busy} onClick={save}>Save ({picked.size})</AccentButton>
            <AccentButton icon={X} variant="soft" disabled={busy} onClick={() => setEditing(false)}>Cancel</AccentButton>
          </div>
        </div>
      )}
    </SoftCard>
  );
}
